import { createFileRoute } from "@tanstack/react-router";
import { serviceSchemaScripts } from "@/lib/service-schema";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";
import { ArrowUpRight, Check } from "lucide-react";
import pharmacyMockupAsset from "@/assets/pharmacy-valuation-imac.png.asset.json";

const PUBLISHED = "2026-09-23T00:00:00+00:00";
const MODIFIED = "2026-10-03T00:00:00+00:00";

const TITLE = "Lead Generation Calculators for B2B Marketing | Square Migration";
const DESCRIPTION =
  "Custom lead generation calculators that turn pricing, ROI, savings or valuation logic into a useful buyer experience and qualified leads.";

const PHARMACY_FEATURES = [
  "Revenue and EBITDA inputs",
  "Estimated valuation range",
  "Useful result before lead capture",
  "Qualified seller context for follow-up",
];

const FAQS: FaqItem[] = [
  {
    question: "What is a lead generation calculator?",
    answer:
      "It's an interactive tool on your website that gives visitors a personalized answer, a savings estimate, an ROI projection, a valuation, in exchange for their contact details. Unlike a gated PDF, the visitor gets real value first, which is why calculators convert at a multiple of traditional forms.",
  },
  {
    question: "How accurate are lead generation calculators?",
    answer:
      "A well-built calculator is a planning model, not a promise. The best ones use your real pricing, benchmarks or scoring logic, so the numbers a prospect sees are credible and specific to them. That credibility is exactly why calculators convert at a multiple of traditional forms.",
  },
  {
    question: "What does a custom lead generation calculator cost to build?",
    answer:
      "It depends on the logic and integrations, but most single-purpose calculators ship in weeks, not quarters. Bring your pricing model or spreadsheet to a call and we'll scope it, you'll know the cost and timeline before anything is built.",
  },
  {
    question: "Where do the leads go when someone uses my calculator?",
    answer:
      "Each submission includes the visitor's inputs and their result, routed to your CRM or inbox. Your sales team follows up with real context, what the prospect calculated and why it matters to them, instead of a bare email address.",
  },
];

export const Route = createFileRoute("/lead-generation-calculator")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/lead-generation-calculator" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [
      { rel: "canonical", href: "https://www.squaremigration.com/lead-generation-calculator" },
    ],
    scripts: [
      ...serviceSchemaScripts({
        path: "/lead-generation-calculator",
        name: "Lead Generation Calculators",
        description: DESCRIPTION,
        serviceType: "Interactive lead generation calculator development services",
      }),
      faqSchemaScript(FAQS),
      webPageSchemaScript({
        path: "/lead-generation-calculator",
        name: TITLE,
        description: DESCRIPTION,
        published: PUBLISHED,
        modified: MODIFIED,
      }),
    ],
  }),
  component: LeadGenerationCalculatorPage,
});

function LeadGenerationCalculatorPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Lead Generation Calculators"
        title="Turn the numbers buyers need into qualified leads."
        description="Interactive calculators give prospects a useful answer before asking them to take the next step. We build valuation, ROI, savings and pricing tools around your real business logic."
        bullets={["Built for your domain", "Branded to your company", "Leads routed to your CRM", "Live in weeks, not quarters"]}
      />

      <section className="border-t border-border/50 bg-background/95 px-6 py-16 md:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 grid items-end gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Featured Project</p>
              <h2 className="text-3xl font-semibold leading-tight sm:text-4xl">Pharmacy <span className="text-primary">Valuation Tool</span></h2>
            </div>
            <p className="leading-relaxed text-muted-foreground lg:text-lg">A branded calculator for Pharmacy CBS that gives pharmacy owners an estimated valuation range and gives the advisory team meaningful context for the first conversation.</p>
          </div>
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div className="flex items-center">
              <img src={pharmacyMockupAsset.url} alt="Pharmacy Valuation Tool displayed on a desktop computer" className="h-auto w-full object-contain" loading="lazy" />
            </div>
            <div>
              <p className="mb-6 text-lg leading-relaxed text-foreground/90">The experience turns a complex, high-stakes question into a clear starting point. Owners enter a few business figures, receive a useful estimate and can continue the conversation with Pharmacy CBS.</p>
              <ul className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {PHARMACY_FEATURES.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>
                    {item}
                  </li>
                ))}
              </ul>
              <a href="https://assessment.pharmacycbs.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-primary/60 px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-primary/10">
                Explore the Pharmacy Valuation Tool <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ProcessSection
        eyebrow="From Question to Asset"
        title="Here's how your calculator gets built."
        lead="Your calculator runs on your domain, branded to you, with logic built from your real pricing and benchmarks."

        steps={[
          { number: "01", title: "Pin the Question", description: "Name the one calculation your prospects keep asking about before they'll talk to sales." },
          { number: "02", title: "Model the Logic", description: "We translate your pricing, benchmarks or scoring model into inputs and outputs a buyer can trust." },
          { number: "03", title: "Design & Ship", description: "Branded, mobile-friendly, and hosted on your domain, usually live within a few weeks." },
          { number: "04", title: "Route the Leads", description: "Results and submitted inputs land in your CRM or inbox so follow-up starts with real context." },
        ]}
      />
      <ClosingCTA
        eyebrow="Build Your Calculator"
        headline={
          <>
            Ready to turn your traffic into <span className="text-primary">qualified leads?</span>
          </>
        }
        subhead="Let's scope your calculator."
        body="Bring the question your buyers keep asking, or even a rough spreadsheet. In one call we'll tell you whether it makes a strong calculator and what it would take to build."
        ctaLabel="Schedule a Call"
      />
      <FaqSection items={FAQS} />
      <RelatedServices current="/lead-generation-calculator" />
      <Footer />
    </main>
  );
}
